import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, TrendingUp, TrendingDown, Calendar, DollarSign, Users, Target, AlertCircle, CheckCircle } from 'lucide-react';

interface AnalysisData {
  company: string;
  ticker: string;
  date: string;
  time: string;
  expectedEPS: string;
  sector: string;
  marketCap: string;
  revenueEstimate: string;
  overview: string;
  keyMetrics: {
    label: string;
    value: string;
    trend: 'up' | 'down' | 'neutral';
  }[];
  strengths: string[];
  risks: string[];
  sentiment: 'bullish' | 'neutral' | 'bearish';
  analystConsensus: string;
  quarterHighlights: string[];
}

// TODO: Replace with real data from API based on earningId
const mockAnalysisData: AnalysisData = {
  company: 'Apple Inc.',
  ticker: 'AAPL',
  date: 'Dec 12, 2025',
  time: 'After Market Close',
  expectedEPS: '$1.54',
  sector: 'Technology',
  marketCap: '$3.2T',
  revenueEstimate: '$119.5B',
  overview: 'Apple is expected to report strong Q4 results driven by robust iPhone 15 sales, continued services growth, and emerging market expansion. The company has demonstrated resilience in a challenging macro environment with its premium positioning and ecosystem lock-in.',
  keyMetrics: [
    { label: 'Revenue Growth (YoY)', value: '+8.2%', trend: 'up' },
    { label: 'iPhone Sales', value: '$68B', trend: 'up' },
    { label: 'Services Revenue', value: '$22.3B', trend: 'up' },
    { label: 'Gross Margin', value: '45.8%', trend: 'neutral' },
    { label: 'Operating Margin', value: '30.2%', trend: 'up' },
    { label: 'Free Cash Flow', value: '$28.5B', trend: 'up' },
  ],
  strengths: [
    'Strong iPhone 15 Pro demand with advanced camera features and USB-C adoption',
    'Services segment showing consistent double-digit growth with expanded subscription base',
    'Successful product launches including Apple Vision Pro gaining traction',
    'Robust ecosystem creates high customer retention and cross-selling opportunities',
    'Strong balance sheet with significant cash reserves for shareholder returns'
  ],
  risks: [
    'Regulatory scrutiny in EU and US regarding App Store practices',
    'Dependence on Chinese manufacturing amid geopolitical tensions',
    'Slowing growth in key markets like China and Europe',
    'Increasing competition in premium smartphone segment',
    'Potential margin pressure from component costs and currency headwinds'
  ],
  sentiment: 'bullish',
  analystConsensus: 'Strong Buy - 32 Buy, 8 Hold, 2 Sell',
  quarterHighlights: [
    'iPhone 15 cycle showing stronger than expected adoption rates',
    'Services attach rate improving with bundled offerings',
    'Wearables and Home products maintaining steady growth trajectory',
    'R&D investments in AI and spatial computing showing promise',
    'Capital allocation strategy remains shareholder friendly'
  ]
};

export function EarningsDetailPage() {
  const navigate = useNavigate();
  const { id: _id } = useParams();

  // TODO: Fetch actual data based on id
  const analysisData = mockAnalysisData;

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'bullish':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'bearish':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <div className="w-4 h-4 bg-gray-400 rounded-full" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white border-b border-gray-200 sticky top-0 z-10"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/earnings')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <h2 className="tracking-tight">{analysisData.company}</h2>
                <span className="px-3 py-1 bg-gray-100 rounded-lg text-gray-600">
                  {analysisData.ticker}
                </span>
              </div>
            </div>
            <div className={`px-4 py-2 rounded-xl border ${getSentimentColor(analysisData.sentiment)}`}>
              {analysisData.sentiment.charAt(0).toUpperCase() + analysisData.sentiment.slice(1)} Outlook
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <h1 className="mb-6 text-5xl md:text-6xl tracking-tight">
            Earnings Analysis
          </h1>
          
          {/* Key Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-6 bg-white border border-gray-200 rounded-2xl">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Calendar className="w-4 h-4" />
                <span>Date</span>
              </div>
              <p className="text-gray-900">{analysisData.date}</p>
              <p className="text-gray-500">{analysisData.time}</p>
            </div>
            <div className="p-6 bg-white border border-gray-200 rounded-2xl">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <DollarSign className="w-4 h-4" />
                <span>Expected EPS</span>
              </div>
              <p className="text-2xl text-gray-900 tracking-tight">{analysisData.expectedEPS}</p>
            </div>
            <div className="p-6 bg-white border border-gray-200 rounded-2xl">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <TrendingUp className="w-4 h-4" />
                <span>Revenue Est.</span>
              </div>
              <p className="text-2xl text-gray-900 tracking-tight">{analysisData.revenueEstimate}</p>
            </div>
            <div className="p-6 bg-white border border-gray-200 rounded-2xl">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Users className="w-4 h-4" />
                <span>Market Cap</span>
              </div>
              <p className="text-2xl text-gray-900 tracking-tight">{analysisData.marketCap}</p>
            </div>
          </div>
        </motion.div>

        {/* Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <h3 className="mb-4 text-gray-900">Executive Summary</h3>
          <div className="p-8 bg-white border border-gray-200 rounded-3xl">
            <p className="text-gray-700 leading-relaxed">{analysisData.overview}</p>
          </div>
        </motion.div>

        {/* Key Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12"
        >
          <h3 className="mb-4 text-gray-900">Key Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analysisData.keyMetrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.05 }}
                className="p-6 bg-white border border-gray-200 rounded-2xl"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-gray-500">{metric.label}</p>
                  {getTrendIcon(metric.trend)}
                </div>
                <p className="text-2xl text-gray-900 tracking-tight">{metric.value}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Quarter Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-12"
        >
          <h3 className="mb-4 text-gray-900">Quarter Highlights</h3>
          <div className="p-8 bg-white border border-gray-200 rounded-3xl space-y-4">
            {analysisData.quarterHighlights.map((highlight, index) => (
              <div key={index} className="flex items-start gap-3">
                <Target className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <p className="text-gray-700">{highlight}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Strengths and Risks Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12"
        >
          {/* Strengths */}
          <div>
            <h3 className="mb-4 text-gray-900">Key Strengths</h3>
            <div className="p-8 bg-white border border-gray-200 rounded-3xl space-y-4">
              {analysisData.strengths.map((strength, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{strength}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Risks */}
          <div>
            <h3 className="mb-4 text-gray-900">Key Risks</h3>
            <div className="p-8 bg-white border border-gray-200 rounded-3xl space-y-4">
              {analysisData.risks.map((risk, index) => (
                <div key={index} className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{risk}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Analyst Consensus */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <h3 className="mb-4 text-gray-900">Analyst Consensus</h3>
          <div className="p-8 bg-gray-900 text-white rounded-3xl">
            <p className="text-2xl tracking-tight">{analysisData.analystConsensus}</p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

